import { Component, ComponentType } from "@/types";
import type { AreaChartProperties } from "@/types/chart-properties";
import { ChartPropertyChange, commonChartHandler } from "./CommonChartHandler";
import { ChartDataPoint } from "../ChartDataManager";
import { colorMixin, axisMixin, animationMixin, mergeMixinResults } from "./mixins";

/**
 * Area chart specific command handler
 */
export class AreaChartHandler {
  /**
   * Phase 2.1: Line thickness preset values
   * Maps named thickness keywords to numeric strokeWidth values
   */
  private readonly thicknessPresets: Record<string, number> = {
    'hairline': 0.5,
    'thin': 1,
    'light': 1.5,
    'normal': 2,
    'default': 2,
    'medium': 2.5,
    'thick': 3,
    'heavy': 4,
    'bold': 4,
    'extra-thick': 5,
    'ultra-thick': 6,
  };

  /**
   * Phase 2.1: Line style preset values
   * Maps style names to strokeDasharray values
   */
  private readonly lineStylePresets: Record<string, string> = {
    'solid': '',
    'continuous': '',
    'dashed': '5 5',
    'dotted': '2 2',
    'dot': '2 2',
    'long-dash': '10 5',
    'short-dash': '3 3',
    'dash-dot': '5 2 2 2',
    'dash-dot-dot': '5 2 2 2 2 2',
    'sparse': '15 10',
    'dense': '2 1',
    'morse': '10 2 2 2 10 2',
  };

  /**
   * Phase 2.1: Marker shape mapping
   * Maps shape names to Recharts-compatible values
   */
  private readonly markerShapes: Record<string, string> = {
    'circle': 'circle',
    'round': 'circle',
    'dot': 'circle',
    'square': 'square',
    'rect': 'square',
    'rectangle': 'square',
    'diamond': 'diamond',
    'rhombus': 'diamond',
    'triangle': 'triangle',
    'tri': 'triangle',
    'star': 'star',
    'cross': 'cross',
    'plus': 'cross',
    'x': 'cross',
    'wye': 'wye',
  };

  /**
   * Phase 2.1: Named marker size presets
   */
  private readonly markerSizePresets: Record<string, number> = {
    'tiny': 2,
    'small': 4,
    'medium': 6,
    'large': 8,
    'big': 8,
    'huge': 12,
    'extra-large': 10,
  };

  /**
   * Phase 2.1: Curve type mapping
   * Maps curve names to Recharts curve types
   * @see https://recharts.org/en-US/api/Area#type
   */
  private readonly curveTypes: Record<string, string> = {
    'linear': 'linear',
    'straight': 'linear',
    'angular': 'linear',
    'monotone': 'monotone',
    'monotonex': 'monotoneX',
    'monotoney': 'monotoneY',
    'smooth': 'monotone',
    'natural': 'natural',
    'basis': 'basis',
    'basisopen': 'basisOpen',
    'basisclosed': 'basisClosed',
    'bundle': 'bundle',
    'cardinal': 'cardinal',
    'cardinalopen': 'cardinalOpen',
    'cardinalclosed': 'cardinalClosed',
    'catmullrom': 'catmullRom',
    'catmull-rom': 'catmullRom',
    'catmullromopen': 'catmullRomOpen',
    'catmullromclosed': 'catmullRomClosed',
    'step': 'step',
    'stepbefore': 'stepBefore',
    'step-before': 'stepBefore',
    'stepafter': 'stepAfter',
    'step-after': 'stepAfter',
    'stairs': 'step',
  };

  /**
   * Phase 2.1: Named tension presets for cardinal curves
   */
  private readonly tensionPresets: Record<string, number> = {
    'tight': 0.9,
    'firm': 0.7,
    'normal': 0.5,
    'loose': 0.3,
    'relaxed': 0.2,
    'very-loose': 0.1,
  };

  /**
   * Parse area chart specific commands
   */
  parseCommand(command: string, component: Component): ChartPropertyChange | null {
    // First try common commands
    const commonChanges = commonChartHandler.parseCommand(command, component);

    // Apply mixins
    const colorChanges = colorMixin.parseCommand(command, component);
    const axisChanges = axisMixin.parseCommand(command, component);
    const animationChanges = animationMixin.parseCommand(command, component);

    // Then apply area-specific commands
    const areaChanges = this.parseAreaSpecificCommand(command, component);

    // Merge all changes
    const mixinChanges = mergeMixinResults(colorChanges, axisChanges, animationChanges);

    if (commonChanges || mixinChanges || areaChanges) {
      return {
        ...commonChanges,
        properties: {
          ...commonChanges?.properties,
          ...mixinChanges,
          ...areaChanges?.properties,
        },
        style: {
          ...commonChanges?.style,
          ...areaChanges?.style,
        },
        data: commonChanges?.data || areaChanges?.data,
        type: commonChanges?.type || areaChanges?.type,
      };
    }

    return null;
  }

  private parseAreaSpecificCommand(command: string, component: Component): ChartPropertyChange | null {
    const lowerCommand = command.toLowerCase();
    const changes: ChartPropertyChange = {};

    // Fill opacity
    if (this.isOpacityCommand(lowerCommand)) {
      const opacityChanges = this.parseOpacityCommand(command, component);
      if (opacityChanges) {
        changes.properties = { ...changes.properties, ...opacityChanges };
      }
    }

    // Stacking
    if (this.isStackCommand(lowerCommand)) {
      const stackChanges = this.parseStackCommand(command);
      if (stackChanges) {
        changes.properties = { ...changes.properties, ...stackChanges };
      }
    }

    // Gradient fill
    if (this.isGradientCommand(lowerCommand)) {
      const gradientChanges = this.parseGradientCommand(command);
      if (gradientChanges) {
        changes.properties = { ...changes.properties, ...gradientChanges };
      }
    }

    // Phase 2.2: Enhanced border line styling
    if (this.isEnhancedLineCommand(lowerCommand)) {
      const lineChanges = this.parseEnhancedLineCommand(command, component);
      if (lineChanges) {
        changes.properties = { ...changes.properties, ...lineChanges };
      }
    }

    // Phase 2.3: Data point markers (NEW)
    if (this.isMarkerCommand(lowerCommand)) {
      const markerChanges = this.parseMarkerCommand(command, component);
      if (markerChanges) {
        changes.properties = { ...changes.properties, ...markerChanges };
      }
    }

    // Stream graph
    if (this.isStreamCommand(lowerCommand)) {
      const streamChanges = this.parseStreamCommand(command);
      if (streamChanges) {
        changes.properties = { ...changes.properties, ...streamChanges };
      }
    }

    // Baseline
    if (this.isBaselineCommand(lowerCommand)) {
      const baselineChanges = this.parseBaselineCommand(command);
      if (baselineChanges) {
        changes.properties = { ...changes.properties, ...baselineChanges };
      }
    }

    // Multiple areas
    if (this.isMultiAreaCommand(lowerCommand)) {
      const multiAreaChanges = this.parseMultiAreaCommand(command, component);
      if (multiAreaChanges) {
        changes.properties = { ...changes.properties, ...multiAreaChanges.properties };
        if (multiAreaChanges.data) {
          changes.data = multiAreaChanges.data;
        }
      }
    }

    // Phase 2.4: Enhanced curve types
    if (this.isEnhancedSmoothingCommand(lowerCommand)) {
      const smoothingChanges = this.parseEnhancedSmoothingCommand(command);
      if (smoothingChanges) {
        changes.properties = { ...changes.properties, ...smoothingChanges };
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isOpacityCommand(command: string): boolean {
    return /opacity|transparent|translucent|opaque|fill.*alpha/.test(command);
  }

  private parseOpacityCommand(command: string, component: Component): Partial<AreaChartProperties> | null {
    const changes: Partial<AreaChartProperties> = {};
    const currentOpacity = typeof component.properties?.fillOpacity === 'number'
      ? component.properties.fillOpacity
      : 0.6;

    // Specific opacity value
    const opacityMatch = command.match(/opacity\s+(?:of\s+|to\s+)?(\d+(?:\.\d+)?)\s*%?/i) ||
                        command.match(/(\d+(?:\.\d+)?)\s*%?\s+opac/i);
    if (opacityMatch) {
      let opacity = parseFloat(opacityMatch[1]);
      if (opacity > 1) opacity = opacity / 100; // Convert percentage
      changes.fillOpacity = opacity;
    }

    // Relative opacity
    if (command.includes("more opaque") || command.includes("less transparent")) {
      changes.fillOpacity = Math.min(1, currentOpacity + 0.2);
    } else if (command.includes("more transparent") || command.includes("less opaque")) {
      changes.fillOpacity = Math.max(0.1, currentOpacity - 0.2);
    }

    // Preset opacity levels
    if (command.includes("fully opaque") || command.includes("solid fill")) {
      changes.fillOpacity = 1;
    } else if (command.includes("semi-transparent") || command.includes("half")) {
      changes.fillOpacity = 0.5;
    } else if (command.includes("very transparent") || command.includes("subtle")) {
      changes.fillOpacity = 0.2;
    } else if (command.includes("barely visible") || command.includes("ghost")) {
      changes.fillOpacity = 0.1;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isStackCommand(command: string): boolean {
    return /stack|cumulative|layer|pile/.test(command);
  }

  private parseStackCommand(command: string): Partial<AreaChartProperties> | null {
    const changes: Partial<AreaChartProperties> = {};

    if (command.includes("stack") || command.includes("cumulative") || command.includes("layer")) {
      if (command.includes("unstack") || command.includes("separate") || command.includes("overlap")) {
        changes.stacked = false;
      } else {
        changes.stacked = true;

        // Stack type
        if (command.includes("percentage") || command.includes("percent") || command.includes("100%")) {
          changes.stackType = "percentage";
        } else if (command.includes("expand") || command.includes("normalize")) {
          changes.stackType = "expand";
        } else if (command.includes("wiggle") || command.includes("stream")) {
          changes.stackType = "wiggle";
        } else if (command.includes("silhouette")) {
          changes.stackType = "silhouette";
        } else {
          changes.stackType = "normal";
        }
      }
    }

    // Stack offset
    if (command.includes("center") && command.includes("stack")) {
      changes.stackOffset = "center";
    } else if (command.includes("baseline") && command.includes("zero")) {
      changes.stackOffset = "none";
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isGradientCommand(command: string): boolean {
    return /gradient|fade|blend/.test(command);
  }

  private parseGradientCommand(command: string): Partial<AreaChartProperties> | null {
    const changes: Partial<AreaChartProperties> = {};

    if (command.includes("gradient")) {
      changes.useGradient = true;

      // Gradient direction
      if (command.includes("horizontal")) {
        changes.gradientDirection = "horizontal";
      } else if (command.includes("vertical")) {
        changes.gradientDirection = "vertical";
      } else if (command.includes("radial") || command.includes("circular")) {
        changes.gradientDirection = "radial";
      }

      // Gradient intensity
      if (command.includes("subtle") || command.includes("light")) {
        changes.gradientIntensity = 0.3;
      } else if (command.includes("strong") || command.includes("intense")) {
        changes.gradientIntensity = 0.8;
      } else {
        changes.gradientIntensity = 0.5;
      }

      // Specific gradient colors
      const fromMatch = command.match(/gradient\s+from\s+([#\w]+)/i);
      const toMatch = command.match(/to\s+([#\w]+)/i);
      if (fromMatch && toMatch) {
        changes.gradientFrom = fromMatch[1];
        changes.gradientTo = toMatch[1];
      }
    }

    // Remove gradient
    if (command.includes("no gradient") || command.includes("solid color")) {
      changes.useGradient = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  /**
   * Phase 2.2: Enhanced line command detection
   * Detects thickness, style, opacity, and show/hide commands
   */
  private isEnhancedLineCommand(command: string): boolean {
    return /thick|thin|width|stroke|bold|weight|hairline|heavy|light|medium|double|halve|increase.*(?:thickness|width)|decrease.*(?:thickness|width)|\d+\s*(?:px)?\s+(?:line|stroke|border)|dash|dot|solid|style|continuous|sparse|dense|morse|pattern|opacity|translucent|transparent|faded|opaque|show\s+line|hide\s+line|border/.test(command);
  }

  /**
   * Phase 2.2: Enhanced border line styling parser
   * Supports 12 patterns for thickness, style, and opacity control
   */
  private parseEnhancedLineCommand(command: string, component: Component): Partial<AreaChartProperties> | null {
    const changes: Partial<AreaChartProperties> = {};
    const currentStrokeWidth = typeof component.properties?.strokeWidth === 'number'
      ? component.properties.strokeWidth
      : 2;

    console.log(`[Phase 2.2] Parsing enhanced line command: "${command}", current width: ${currentStrokeWidth}`);

    // Pattern 1: Show/hide border line
    const showLineMatch = command.match(/\b(show|add|with)\s+(?:\w+\s+)*line/i);
    const hideLineMatch = command.match(/\b(hide|no|remove)\s+(?:\w+\s+)*line/i);
    if (showLineMatch || command.includes("show border")) {
      changes.showLine = true;
      console.log(`[Phase 2.2] Show line: true`);
    } else if (hideLineMatch || command.includes("hide border")) {
      changes.showLine = false;
      console.log(`[Phase 2.2] Show line: false`);
    }

    // Pattern 2: Double/halve thickness
    const doubleHalveMatch = command.match(/\b(double|halve)\b.*(?:thickness|width|stroke|line|border)/i);
    if (doubleHalveMatch) {
      const multiplier = doubleHalveMatch[1].toLowerCase() === 'double' ? 2 : 0.5;
      changes.strokeWidth = Math.max(0.5, Math.min(10, currentStrokeWidth * multiplier));
      console.log(`[Phase 2.2] ${doubleHalveMatch[1]} thickness: ${changes.strokeWidth}`);
      return changes;
    }

    // Pattern 3: Increase/decrease by X
    const adjustByMatch = command.match(/\b(increase|decrease)\b.*\bby\s+(\d+(?:\.\d+)?)/i);
    if (adjustByMatch) {
      const amount = parseFloat(adjustByMatch[2]);
      const isIncrease = adjustByMatch[1].toLowerCase() === 'increase';
      changes.strokeWidth = Math.max(0.5, Math.min(10, isIncrease ? currentStrokeWidth + amount : currentStrokeWidth - amount));
      console.log(`[Phase 2.2] Adjust by ${amount}: ${changes.strokeWidth}`);
      return changes;
    }

    // Pattern 4: Specific numeric width value
    const widthMatch = command.match(/(?:width|thickness|stroke)[:\s]+(\d+(?:\.\d+)?)/i) ||
                       command.match(/(\d+(?:\.\d+)?)\s*(?:px)?\s+(?:thick|width|stroke|line|border)/i) ||
                       command.match(/(?:set\s+)?(?:line\s+|border\s+)?(?:to\s+)(\d+(?:\.\d+)?)\s*(?:px)?$/i);
    if (widthMatch) {
      const width = parseFloat(widthMatch[1]);
      changes.strokeWidth = Math.max(0.5, Math.min(10, width));
      console.log(`[Phase 2.2] Numeric width: ${changes.strokeWidth}`);
      return changes;
    }

    // Pattern 5: Named thickness presets
    const presetMatch = command.match(/(?:use\s+|set\s+|make\s+(?:it\s+)?)?(?:line\s+|border\s+)?(hairline|thin|light|normal|default|medium|thick|heavy|bold|extra-thick|ultra-thick)(?:\s+(?:line|stroke|width|border))?/i);
    if (presetMatch) {
      const presetName = presetMatch[1].toLowerCase();
      if (this.thicknessPresets[presetName] !== undefined) {
        changes.strokeWidth = this.thicknessPresets[presetName];
        console.log(`[Phase 2.2] Preset thickness "${presetName}": ${changes.strokeWidth}`);
        return changes;
      }
    }

    // Pattern 6: Percentage adjustment
    const percentMatch = command.match(/(?:make\s+)?(\d+)\s*%\s+(thicker|thinner)/i);
    if (percentMatch) {
      const percent = parseInt(percentMatch[1]) / 100;
      const isThicker = percentMatch[2].toLowerCase() === 'thicker';
      const multiplier = isThicker ? (1 + percent) : (1 - percent);
      changes.strokeWidth = Math.max(0.5, Math.min(10, currentStrokeWidth * multiplier));
      console.log(`[Phase 2.2] Percentage adjustment: ${percentMatch[1]}% ${percentMatch[2]} → ${changes.strokeWidth}`);
      return changes;
    }

    // Pattern 7: Relative thickness
    if (command.includes("much thicker") || command.includes("way thicker")) {
      changes.strokeWidth = Math.min(currentStrokeWidth + 2, 10);
    } else if (command.includes("thicker") || command.includes("bolder") || command.includes("heavier")) {
      changes.strokeWidth = Math.min(currentStrokeWidth + 1, 10);
    } else if (command.includes("much thinner") || command.includes("way thinner")) {
      changes.strokeWidth = Math.max(currentStrokeWidth - 2, 0.5);
    } else if (command.includes("thinner") || command.includes("lighter")) {
      changes.strokeWidth = Math.max(currentStrokeWidth - 1, 0.5);
    }

    // Pattern 8: Named style presets (dashed, dotted, etc.)
    const styleMatch = command.match(/(?:use\s+|make\s+(?:it\s+)?|set\s+)?(?:line\s+|border\s+)?(?:style\s+)?(?:to\s+)?(solid|continuous|dashed|dotted|dot|long-dash|short-dash|dash-dot-dot|dash-dot|sparse|dense|morse)(?:\s+(?:line|style|border))?/i);
    if (styleMatch) {
      const styleName = styleMatch[1].toLowerCase();
      if (this.lineStylePresets[styleName] !== undefined) {
        changes.strokeDasharray = this.lineStylePresets[styleName];
        console.log(`[Phase 2.2] Style preset "${styleName}": "${changes.strokeDasharray}"`);
      }
    }

    // Pattern 9: Custom dash pattern
    const customPatternMatch = command.match(/(?:set\s+)?(?:custom\s+)?dash\s+pattern\s+(?:to\s+)?(\d+(?:\s+\d+)+)/i) ||
                               command.match(/(?:stroke\s+)?(?:dash\s+)?pattern\s+(\d+(?:\s+\d+)+)/i);
    if (customPatternMatch) {
      const pattern = customPatternMatch[1].trim();
      changes.strokeDasharray = pattern;
      console.log(`[Phase 2.2] Custom dash pattern: "${pattern}"`);
    }

    // Pattern 10: Opacity percentage
    const opacityPercentMatch = command.match(/(?:set\s+)?(?:line\s+|border\s+)?opacity\s+(?:to\s+)?(\d+)\s*%/i);
    if (opacityPercentMatch) {
      const opacity = parseInt(opacityPercentMatch[1]) / 100;
      changes.strokeOpacity = Math.max(0, Math.min(1, opacity));
      console.log(`[Phase 2.2] Opacity (percent): ${changes.strokeOpacity}`);
    }

    // Pattern 11: Opacity decimal
    const opacityDecimalMatch = command.match(/(?:set\s+)?(?:line\s+|border\s+)?opacity\s+(?:to\s+)?(0?\.\d+|1(?:\.0)?)/i);
    if (opacityDecimalMatch) {
      const opacity = parseFloat(opacityDecimalMatch[1]);
      changes.strokeOpacity = Math.max(0, Math.min(1, opacity));
      console.log(`[Phase 2.2] Opacity (decimal): ${changes.strokeOpacity}`);
    }

    // Pattern 12: Named opacity levels
    const namedOpacityMatch = command.match(/(?:make\s+)?(?:line\s+|border\s+)?(translucent|semi-transparent|faded|transparent|invisible|opaque|visible)/i);
    if (namedOpacityMatch) {
      const opacityName = namedOpacityMatch[1].toLowerCase();
      const opacityMap: Record<string, number> = {
        'invisible': 0,
        'transparent': 0.1,
        'translucent': 0.4,
        'faded': 0.5,
        'semi-transparent': 0.6,
        'visible': 1,
        'opaque': 1,
      };
      changes.strokeOpacity = opacityMap[opacityName] ?? 1;
      console.log(`[Phase 2.2] Named opacity "${opacityName}": ${changes.strokeOpacity}`);
    }

    if (Object.keys(changes).length > 0) {
      console.log(`[Phase 2.2] Final changes:`, changes);
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  /**
   * Phase 2.3: Marker command detection
   * Detects marker show/hide, shape, size, color, and style commands
   */
  private isMarkerCommand(command: string): boolean {
    return /dot|point|marker|circle|square|diamond|triangle|star|cross|hollow|filled|outlined/.test(command);
  }

  /**
   * Phase 2.3: Data point marker parser
   * Supports 9 patterns for marker customization
   */
  private parseMarkerCommand(command: string, component: Component): Partial<AreaChartProperties> | null {
    const changes: Partial<AreaChartProperties> = {};
    const currentSize = typeof component.properties?.dotSize === 'number'
      ? component.properties.dotSize
      : 4;

    console.log(`[Phase 2.3] Parsing marker command: "${command}", current size: ${currentSize}`);

    // Pattern 1: Show/hide markers
    const showHideMatch = command.match(/(show|hide|display|remove|add|enable|disable)\s+(?:the\s+)?(?:data\s+)?(?:points?|dots?|markers?)/i);
    if (showHideMatch) {
      const action = showHideMatch[1].toLowerCase();
      changes.showDots = ['show', 'display', 'add', 'enable'].includes(action);
      console.log(`[Phase 2.3] Show/hide markers: ${changes.showDots}`);
    }

    // Pattern 2: Marker shape
    const shapeMatch = command.match(/(?:use\s+|set\s+)?(?:marker|dot|point)\s+(?:shape\s+)?(?:to\s+)?(circle|round|dot|square|rect|rectangle|diamond|rhombus|triangle|tri|star|cross|plus|x|wye)/i) ||
                       command.match(/(circle|square|diamond|triangle|star|cross)\s+(?:markers?|dots?|points?)/i);
    if (shapeMatch) {
      const shapeName = shapeMatch[1].toLowerCase();
      if (this.markerShapes[shapeName]) {
        changes.dotShape = this.markerShapes[shapeName];
        console.log(`[Phase 2.3] Marker shape: ${changes.dotShape}`);
      }
    }

    // Pattern 3: Numeric marker size
    const numericSizeMatch = command.match(/(?:marker|dot|point)\s+size\s+(?:to\s+)?(\d+)/i) ||
                             command.match(/(\d+)\s*(?:px)?\s+(?:markers?|dots?|points?)/i) ||
                             command.match(/(?:set\s+)?(?:marker|dot|point)\s+(?:size\s+)?(?:to\s+)?(\d+)/i);
    if (numericSizeMatch) {
      changes.dotSize = Math.max(1, Math.min(20, parseInt(numericSizeMatch[1])));
      console.log(`[Phase 2.3] Numeric marker size: ${changes.dotSize}`);
    }

    // Pattern 4: Named marker size
    const namedSizeMatch = command.match(/(?:make\s+)?(?:markers?|dots?|points?)\s+(tiny|small|medium|large|big|huge|extra-large)/i) ||
                           command.match(/(tiny|small|medium|large|big|huge)\s+(?:markers?|dots?|points?)/i);
    if (namedSizeMatch) {
      const sizeName = namedSizeMatch[1].toLowerCase();
      if (this.markerSizePresets[sizeName] !== undefined) {
        changes.dotSize = this.markerSizePresets[sizeName];
        console.log(`[Phase 2.3] Named marker size "${sizeName}": ${changes.dotSize}`);
      }
    }

    // Pattern 5: Relative marker size
    if (command.includes("bigger") || command.includes("larger")) {
      changes.dotSize = Math.min(currentSize + 2, 20);
      console.log(`[Phase 2.3] Bigger markers: ${changes.dotSize}`);
    } else if (command.includes("smaller") || command.includes("tinier")) {
      changes.dotSize = Math.max(currentSize - 2, 1);
      console.log(`[Phase 2.3] Smaller markers: ${changes.dotSize}`);
    }

    // Pattern 6: Marker color
    const colorMatch = command.match(/(?:set\s+)?(?:marker|dot|point)\s+colou?r\s+(?:to\s+)?(#[0-9A-Fa-f]{3,6}|\w+)/i) ||
                       command.match(/(#[0-9A-Fa-f]{3,6})\s+(?:markers?|dots?|points?)/i);
    if (colorMatch) {
      changes.dotColor = colorMatch[1];
      console.log(`[Phase 2.3] Marker color: ${changes.dotColor}`);
    }

    // Pattern 7: Hollow/filled markers
    const fillMatch = command.match(/(?:make\s+)?(?:markers?|dots?|points?)\s+(hollow|filled|outlined|solid|empty)/i);
    if (fillMatch) {
      const fillType = fillMatch[1].toLowerCase();
      changes.dotFill = ['filled', 'solid'].includes(fillType) ? 'currentColor' : 'none';
      console.log(`[Phase 2.3] Marker fill: ${changes.dotFill}`);
    }

    // Pattern 8: Marker stroke width
    const strokeMatch = command.match(/(?:marker|dot|point)\s+(?:stroke|border|outline)\s+(?:width\s+)?(?:to\s+)?(\d+)/i) ||
                        command.match(/(\d+)\s*(?:px)?\s+(?:marker|dot|point)\s+(?:stroke|border|outline)/i);
    if (strokeMatch) {
      changes.dotStrokeWidth = Math.max(0, Math.min(5, parseInt(strokeMatch[1])));
      console.log(`[Phase 2.3] Marker stroke width: ${changes.dotStrokeWidth}`);
    }

    // Pattern 9: Conditional marker visibility
    const conditionalMatch = command.match(/(?:show\s+)?(?:markers?|dots?)\s+(?:only\s+)?(?:on|at)\s+(first|last|min|max|peaks?|valleys?|extremes?)/i);
    if (conditionalMatch) {
      const condition = conditionalMatch[1].toLowerCase();
      changes.dotVisibility = condition.replace(/s$/, ''); // normalize plurals
      console.log(`[Phase 2.3] Conditional marker visibility: ${changes.dotVisibility}`);
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isStreamCommand(command: string): boolean {
    return /stream|flow|river/.test(command);
  }

  private parseStreamCommand(command: string): Partial<AreaChartProperties> | null {
    const changes: Partial<AreaChartProperties> = {};

    if (command.includes("stream") || command.includes("flow") || command.includes("river")) {
      changes.chartVariant = "stream";
      changes.stacked = true;
      changes.stackType = "wiggle";
      changes.stackOffset = "silhouette";
      changes.showLine = false;

      // Stream interpolation
      if (command.includes("smooth")) {
        changes.lineType = "basis";
      } else {
        changes.lineType = "cardinal";
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isBaselineCommand(command: string): boolean {
    return /baseline|base|zero|reference/.test(command);
  }

  private parseBaselineCommand(command: string): Partial<AreaChartProperties> | null {
    const changes: Partial<AreaChartProperties> = {};

    // Baseline value
    const baselineMatch = command.match(/baseline\s+(?:at\s+)?(-?\d+(?:\.\d+)?)/i);
    if (baselineMatch) {
      changes.baselineValue = parseFloat(baselineMatch[1]);
    }

    // Baseline type
    if (command.includes("zero baseline") || command.includes("start at zero")) {
      changes.baselineValue = 0;
    } else if (command.includes("minimum baseline")) {
      changes.baselineType = "min";
    } else if (command.includes("average baseline") || command.includes("mean baseline")) {
      changes.baselineType = "average";
    }

    // Show baseline
    if (command.includes("show baseline") || command.includes("add baseline")) {
      changes.showBaseline = true;
    } else if (command.includes("hide baseline") || command.includes("no baseline")) {
      changes.showBaseline = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isMultiAreaCommand(command: string): boolean {
    return /add.*area|another.*area|multiple|series|compare/.test(command);
  }

  private parseMultiAreaCommand(command: string, component: Component): ChartPropertyChange | null {
    const props: Partial<AreaChartProperties> = {};
    const changes: ChartPropertyChange = { properties: props };
    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];
    const rawAreaKeys = component.properties?.areaKeys;
    const areaKeys = Array.isArray(rawAreaKeys)
      ? rawAreaKeys.filter((k): k is string => typeof k === 'string')
      : ["value"];

    // Add another area series
    if (command.includes("add") && command.includes("area")) {
      // Parse series name
      const nameMatch = command.match(/area\s+(?:for|called|named)\s+["']([^"']+)["']/i) ||
                       command.match(/["']([^"']+)["']\s+area/i);
      const seriesName = nameMatch ? nameMatch[1] : `Series ${areaKeys.length + 1}`;
      const seriesKey = seriesName.toLowerCase().replace(/\s+/g, "_");

      // Add to areaKeys
      if (!areaKeys.includes(seriesKey)) {
        props.areaKeys = [...areaKeys, seriesKey];

        // Add sample data for the new area
        if (currentData.length > 0) {
          changes.data = currentData.map(point => ({
            ...point,
            [seriesKey]: Math.floor(Math.random() * 100) + 20,
          }));
        }
      }
    }

    // Comparison areas
    if (command.includes("compare") || command.includes("versus")) {
      const compareMatch = command.match(/compare\s+["']([^"']+)["']\s+(?:with|to)\s+["']([^"']+)["']/i);
      if (compareMatch) {
        const series1 = compareMatch[1].toLowerCase().replace(/\s+/g, "_");
        const series2 = compareMatch[2].toLowerCase().replace(/\s+/g, "_");

        props.areaKeys = [series1, series2];
        props.stacked = false; // Don't stack for comparison
      }
    }

    return Object.keys(props).length > 0 ? changes : null;
  }

  /**
   * Phase 2.4: Enhanced smoothing command detection
   * Detects curve types, tension, and connect nulls commands
   */
  private isEnhancedSmoothingCommand(command: string): boolean {
    return /smooth|curve|interpolat|linear|step|straight|natural|basis|bundle|cardinal|catmull|monotone|tension|connect.*(?:gap|null)|skip.*(?:gap|null)|break.*(?:gap|null)/.test(command);
  }

  /**
   * Phase 2.4: Enhanced curve type parser
   * Supports 26 curve types plus tension and connect nulls
   */
  private parseEnhancedSmoothingCommand(command: string): Partial<AreaChartProperties> | null {
    const changes: Partial<AreaChartProperties> = {};

    console.log(`[Phase 2.4] Parsing curve/smoothing command: "${command}"`);

    // Pattern 1: Named tension values (check before curve types to avoid "smooth" matching)
    const tensionNamedMatch = command.match(/\b(tight|firm|loose|relaxed|very-loose)\s+(?:curve|smoothing|tension)/i);
    if (tensionNamedMatch) {
      const tensionName = tensionNamedMatch[1].toLowerCase();
      if (this.tensionPresets[tensionName] !== undefined) {
        changes.lineType = 'cardinal';
        changes.curveTension = this.tensionPresets[tensionName];
        console.log(`[Phase 2.4] Named tension "${tensionName}": ${changes.curveTension}`);
        return changes;
      }
    }

    // Pattern 2: Step variants (longer patterns first)
    const stepVariantMatch = command.match(/\b(stepbefore|step-before|stepafter|step-after)\b/i);
    if (stepVariantMatch) {
      const variant = stepVariantMatch[1].toLowerCase();
      if (variant === 'stepbefore' || variant === 'step-before') {
        changes.lineType = 'stepBefore';
      } else {
        changes.lineType = 'stepAfter';
      }
      console.log(`[Phase 2.4] Step variant: ${changes.lineType}`);
      return changes;
    }

    // Pattern 3: Step with direction
    const stepDirectionMatch = command.match(/\bstep\s+(before|after|center|middle)\b/i);
    if (stepDirectionMatch) {
      const stepType = stepDirectionMatch[1].toLowerCase();
      if (stepType === 'before') {
        changes.lineType = 'stepBefore';
      } else if (stepType === 'after') {
        changes.lineType = 'stepAfter';
      } else {
        changes.lineType = 'step';
      }
      console.log(`[Phase 2.4] Step direction: ${changes.lineType}`);
      return changes;
    }

    // Pattern 4: Named curve types (order matters - longer patterns first)
    const curveTypeMatch = command.match(/(?:use\s+|set\s+)?(?:line\s+)?(?:type\s+|curve\s+|interpolation\s+)?(?:to\s+)?(linear|straight|angular|monotonex|monotoney|monotone|smooth|natural|basisopen|basisclosed|basis|bundle|cardinalopen|cardinalclosed|cardinal|catmullromopen|catmullromclosed|catmullrom|catmull-rom|step|stairs)/i);
    if (curveTypeMatch) {
      const typeName = curveTypeMatch[1].toLowerCase();
      if (this.curveTypes[typeName]) {
        changes.lineType = this.curveTypes[typeName];
        console.log(`[Phase 2.4] Curve type: ${changes.lineType}`);
        return changes;
      }
    }

    // Pattern 5: Numeric tension value
    const tensionNumericMatch = command.match(/(?:set\s+)?(?:curve\s+)?tension\s+(?:to\s+)?(\d+(?:\.\d+)?)/i);
    if (tensionNumericMatch) {
      const tension = parseFloat(tensionNumericMatch[1]);
      changes.lineType = 'cardinal';
      changes.curveTension = Math.max(0, Math.min(1, tension > 1 ? tension / 100 : tension));
      console.log(`[Phase 2.4] Numeric tension: ${changes.curveTension}`);
      return changes;
    }

    // Pattern 6: Connect nulls
    if (command.includes("connect") && (command.includes("gap") || command.includes("null") || command.includes("missing"))) {
      changes.connectNulls = true;
      console.log(`[Phase 2.4] Connect nulls: true`);
    } else if (command.includes("skip") && (command.includes("gap") || command.includes("null") || command.includes("missing"))) {
      changes.connectNulls = false;
      console.log(`[Phase 2.4] Connect nulls: false`);
    } else if (command.includes("break") && (command.includes("gap") || command.includes("null"))) {
      changes.connectNulls = false;
      console.log(`[Phase 2.4] Break at gaps: connectNulls = false`);
    }

    // Fallback: simple keyword matching (backward compatibility)
    if (changes.lineType === undefined && Object.keys(changes).length === 0) {
      if (command.includes("very smooth") || command.includes("extra smooth")) {
        changes.lineType = "basis";
      } else if (command.includes("smooth") || command.includes("curved")) {
        changes.lineType = "monotone";
      } else if (command.includes("linear") || command.includes("straight")) {
        changes.lineType = "linear";
      } else if (command.includes("natural")) {
        changes.lineType = "natural";
      }

      if (changes.lineType) {
        console.log(`[Phase 2.4] Fallback curve type: ${changes.lineType}`);
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  /**
   * Get area chart specific suggestions
   */
  getSuggestions(component: Component): string[] {
    const suggestions: string[] = [
      ...commonChartHandler.getSuggestions(component),
      ...axisMixin.getSuggestions(component),
      ...colorMixin.getSuggestions(component),
    ];

    // Area-specific suggestions
    const fillOpacity = typeof component.properties?.fillOpacity === 'number'
      ? component.properties.fillOpacity
      : 0.6;

    if (fillOpacity > 0.5) {
      suggestions.push("Make more transparent");
    } else {
      suggestions.push("Make more opaque");
    }

    if (component.properties?.stacked) {
      suggestions.push("Unstack areas", "Show as percentage stack");
    } else {
      suggestions.push("Stack the areas", "Create stream graph");
    }

    if (!component.properties?.useGradient) {
      suggestions.push("Add gradient fill");
    } else {
      suggestions.push("Use solid fill");
    }

    if (!component.properties?.showLine) {
      suggestions.push("Show line on top");
    }

    suggestions.push(
      "Set fill opacity to 0.3",
      "Smooth the curves",
      "Add baseline at zero",
      "Add another area series",
      "Use step interpolation"
    );

    // =========================================
    // Phase 2 styling suggestions
    // =========================================
    suggestions.push(
      "Use thick border line",
      "Use hairline border",
      "Add dashed border",
      "Dotted edge line",
      "Border opacity 50%",
      "Show data point markers",
      "Use square markers",
      "Diamond markers",
      "Large markers",
      "Make markers hollow",
      "Use catmullRom curve",
      "Use cardinal curve",
      "Tight curve",
      "Loose smoothing",
      "Connect gaps"
    );

    const rawAreaKeys2 = component.properties?.areaKeys;
    const areaKeys = Array.isArray(rawAreaKeys2)
      ? rawAreaKeys2.filter((k): k is string => typeof k === 'string')
      : ["value"];
    if (areaKeys.length === 1) {
      suggestions.push('Add area for "Previous Year"');
    }

    return suggestions;
  }
}

export const areaChartHandler = new AreaChartHandler();
